import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, TextInput,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import Modal from '@/components/DetailsModal';
import EmailBodyEditor from '../EmailBodyEditor';
import styles from './index.module.css';

/**
 * Edits the email record in Equity only - it does not change the message that
 * was already delivered to the agent's mailbox.
 */
const EditAgentEmail = (props) => {
  const {
    email,
    opened,
    close,
    onSave,
    loading,
  } = props;

  const t = prepareTranslate();
  const contentRef = useRef(null);

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [keptFiles, setKeptFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  useEffect(() => {
    if (!opened) return;

    setSubject(email?.name || '');
    setContent(email?.content || '');
    setKeptFiles(email?.files?.filter((file) => file.path) || []);
    setNewFiles([]);
  }, [opened, email]);

  const handleSave = () => {
    onSave({
      name: subject,
      // The body is html - read it back from the editable element as it stands.
      content: contentRef.current?.innerHTML ?? content,
      keptFiles,
      newFiles,
    });
  };

  const actions = [
    <Button key='save' onClick={handleSave} loading={loading} w={120}>
      {t('global.save')}
    </Button>,
    <Button key='cancel' variant='outline' onClick={close} w={120}>
      {t('global.cancel')}
    </Button>,
  ];

  return (
    <Modal
      opened={opened}
      close={close}
      actions={actions}
      label={t('components.viewAgentEmails.edit.label')}
    >
      <Alert
        icon={<IconInfoCircle />}
        color='blue'
        variant='light'
        className={styles.notice}
      >
        {t('components.viewAgentEmails.edit.notice')}
      </Alert>

      <TextInput
        label={t('components.viewAgentEmails.edit.subject')}
        value={subject}
        onChange={(event) => setSubject(event.currentTarget.value)}
        className={styles.subject}
      />

      <EmailBodyEditor
        label={t('components.viewAgentEmails.edit.content')}
        content={content}
        contentRef={contentRef}
        keptFiles={keptFiles}
        newFiles={newFiles}
        onAddFiles={(previews) => setNewFiles((prev) => [...prev, ...previews])}
        onRemoveKeptFile={(index) => setKeptFiles(
          (prev) => prev.filter((_, i) => i !== index),
        )}
        onRemoveNewFile={(index) => setNewFiles((prev) => prev.filter((_, i) => i !== index))}
      />
    </Modal>
  );
};

EditAgentEmail.propTypes = {
  email: PropTypes.object,
  opened: PropTypes.bool,
  close: PropTypes.func,
  onSave: PropTypes.func,
  loading: PropTypes.bool,
};

export default EditAgentEmail;
